import { useCallback } from 'react';
import { useParticipantStore, ParticipantActionTypes } from '../store/participantStore';
import { GetParticipants, GetParticipantById } from '../api/actions';
import { mockParticipants } from '../api/mockData';

/**
 * Custom hook for managing participant data with caching
 */
export const useParticipants = () => {
  const store = useParticipantStore();
  const { participants, loading, error, loadedFromApi, lastUpdated, dispatch } = store;

  /**
   * Load all participants from API or cache
   */
  const loadParticipants = useCallback(async (forceRefresh = false) => {
    // Check if already loaded and not forcing refresh
    if (participants.length > 0 && !forceRefresh) {
      return participants;
    }

    dispatch({ type: ParticipantActionTypes.SET_LOADING, payload: true });

    try {
      const rawData = await GetParticipants();
     
      // Normalize the data - API might return JSON strings in array
      let data = rawData;
      if (Array.isArray(rawData) && rawData.length > 0 && typeof rawData[0] === 'string') {
        data = rawData.map(item => JSON.parse(item));
      } else if (typeof rawData === 'string') {
        data = JSON.parse(rawData);
      }
      
      dispatch({
        type: ParticipantActionTypes.LOAD_PARTICIPANTS,
        payload: data,
        fromApi: true,
      });
      return data;
    } catch (err) {
      
      dispatch({
        type: ParticipantActionTypes.SET_ERROR,
        payload: 'Failed to load participants from API',
      });
      
      dispatch({ type: ParticipantActionTypes.SET_LOADING, payload: false });
      
      throw err; // Don't fall back to mock data - let caller handle the error
    }
  }, [dispatch, participants]);

  /**
   * Get a single participant by ID
   */
  const getParticipantById = useCallback(async (participantId) => {
    // Ensure participants are loaded
    if (participants.length === 0) {
      await loadParticipants();
    }
    
    // Search in cache
    const participant = participants.find(p => p.id === participantId);
    
    if (participant) {
      return participant;
    }

    return null;
  }, [loadParticipants, participants]);

  /**
   * Find participant by email or mobile
   */
  const findParticipant = useCallback(async (emailOrMobile) => {
    // Ensure participants are loaded
    const allParticipants = participants.length > 0 ? participants : await loadParticipants();
    
    return allParticipants.find(
      p => p.emailAddress?.toLowerCase() === emailOrMobile?.toLowerCase() || 
           p.mobile === emailOrMobile
    );
  }, [participants, loadParticipants]);

  /**
   * Update a participant in the store
   */
  const updateParticipant = useCallback((updatedParticipant) => {
    dispatch({
      type: ParticipantActionTypes.UPDATE_PARTICIPANT,
      payload: updatedParticipant,
    });
  }, [dispatch]);

  /**
   * Clear the cache and reload from API
   */
  const clearCache = useCallback(() => {
    dispatch({ type: ParticipantActionTypes.CLEAR_CACHE });
  }, [dispatch]);

  return {
    participants,
    loading,
    error,
    loadedFromApi,
    lastUpdated,
    loadParticipants,
    getParticipantById,
    findParticipant,
    updateParticipant,
    clearCache,
  };
};
